export enum DsmSigningType {
  None = 0,
  DocuSign = 1,
  InPerson = 2,
  DocuSignInPerson = 3
}
export enum DsmSigningTypeName {
  None = 'None',
  DocuSign = 'DocuSign',
  InPerson = 'InPerson',
  DocuSignInPerson = 'DocuSignInPerson'
}

export const DSM_SIGNING_TYPE_LABELS: Record<string, string> = {
  [DsmSigningTypeName.DocuSign]: 'Sent via Email',
  [DsmSigningTypeName.InPerson]: 'In-Person',
  [DsmSigningTypeName.DocuSignInPerson]: 'In-Person'
};

export enum DsmSignOffSubTab {
  Signers = 'signers',
  Addendums = 'addendums'
}

export enum DsmSignOffQueryParam {
  EnvelopeId = 'envelopeId'
}

export enum DsmEnvelopeStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Complete = 'Complete',
  Voided = 'Voided',
  Declined = 'Declined',
  Expired = 'Expired'
}

export enum DsmRecipientStatus {
  Pending = 'Pending',
  Sent = 'Sent',
  Delivered = 'Delivered',
  Complete = 'Complete',
  Declined = 'Declined',
  AutoResponded = 'AutoResponded',
  AuthenticationFailed = 'AuthenticationFailed'
}

export const DSM_RECIPIENT_FAILURE_STATUSES: DsmRecipientStatus[] = [
  DsmRecipientStatus.AutoResponded,
  DsmRecipientStatus.AuthenticationFailed
];

export const DSM_SIGNER_CHIP_LABELS: Record<string, string> = {
  [DsmRecipientStatus.Complete]: 'Signed',
  [DsmRecipientStatus.Declined]: 'Declined',
  [DsmRecipientStatus.AutoResponded]: 'Delivery Failed',
  [DsmRecipientStatus.AuthenticationFailed]: 'Delivery Failed'
};

export const DSM_SIGNER_CHIP_CLASSES: Record<string, string> = {
  [DsmRecipientStatus.Complete]: 'text-emerald-700',
  [DsmRecipientStatus.Declined]: 'text-red-600',
  [DsmRecipientStatus.AutoResponded]: 'text-red-600',
  [DsmRecipientStatus.AuthenticationFailed]: 'text-red-600'
};

export enum DsmAddendumSigningStatus {
  NotSent = 'NotSent',
  Sent = 'Sent',
  Viewed = 'Viewed',
  PartiallySigned = 'PartiallySigned',
  Signed = 'Signed'
}

export enum DsmAddendumBuilderStatus {
  NotRequested = 'NotRequested',
  AwaitingBuilder = 'AwaitingBuilder',
  BuilderSigned = 'BuilderSigned'
}

export enum DsmSignerEntityType {
  Prospect = 'prospect',
  CoBuyer1 = 'cobuyer1',
  CoBuyer2 = 'cobuyer2',
  Project = 'project',
  Executive = 'user',
  SecondaryExecutive = 'secondary_executive',
  SalesAgent = 'salesagent',
  Realtor = 'realtor'
}

export const DSM_SIGNER_ROLE_LABELS: Record<string, string> = {
  [DsmSignerEntityType.Prospect]: 'Buyer',
  [DsmSignerEntityType.CoBuyer1]: 'Co-Buyer 1',
  [DsmSignerEntityType.CoBuyer2]: 'Co-Buyer 2',
  [DsmSignerEntityType.Project]: 'Project',
  [DsmSignerEntityType.Executive]: 'Builder',
  [DsmSignerEntityType.SecondaryExecutive]: 'Secondary Builder',
  [DsmSignerEntityType.SalesAgent]: 'Sales Agent',
  [DsmSignerEntityType.Realtor]: 'Realtor'
};

export type DsmSignOffTagSeverity = 'info' | 'success' | 'danger' | 'warn' | 'secondary';

export interface DsmSignee {
  name: string | null;
  email: string | null;
}

export interface DsmSignOffSigner {
  id: string;
  dealDocumentId: string;
  entityType: string;
  entityID: string;
  status: DsmRecipientStatus;
  sequence: number;
  failureReason?: string | null;
  signee: DsmSignee | null;
  createdAt: string;
  lastModified: string | null;
}

export interface DsmAddendumSummary {
  addendumId: string;
  seq: number;
  builderReference: string | null;
  total: number;
}

export interface DsmAddendumEnvelope {
  dealDocumentId: string;
  envelopeId: string | null;
  status: DsmEnvelopeStatus;
  signingType: DsmSigningTypeName;
  includeBuilder: boolean;
  sourceDealDocumentId: string | null;
  builderStatus: DsmAddendumBuilderStatus;
  canVoid: boolean;
  createdAt: string;
  addendumCount: number;
  totalAmount: number;
  signers: DsmSignOffSigner[];
  addendums: DsmAddendumSummary[];
}

export interface DsmAddendumSignOffStatus {
  addendumId: string;
  dealDocumentId: string | null;
  status: DsmAddendumSigningStatus;
  builderStatus: DsmAddendumBuilderStatus;
  hasMismatch: boolean;
  signers: DsmSignOffSigner[];
  completedAt: string | null;
}

export enum DsmBlockedAddendumReason {
  NotEligible = 'NotEligible',
  TypeExcluded = 'TypeExcluded',
  AlreadyInRequest = 'AlreadyInRequest'
}

export interface DsmBlockedAddendum {
  addendumId: string;
  reason: DsmBlockedAddendumReason;
  existingDealDocumentId: string | null;
}

export interface DsmAddendumSelectionValidation {
  isValid: boolean;
  blockedAddendums: DsmBlockedAddendum[];
}

export interface DsmDetectedSigner {
  entityType: string;
  pages: number[];
}

export interface DsmUploadedDocumentVerification {
  canProceed: boolean;
  blockingErrors: string[];
  warnings: string[];
  detectedSigners: DsmDetectedSigner[];
  missingRequiredSigners: string[];
  pageCount: number;
  fileSizeBytes: number;
  estimatedEnvelopeSizeBytes: number;
}

export interface DsmSendAddendumSigningRequest {
  addendumIds: string[];
  includeBuilder: boolean;
  signingType: DsmSigningType;
  s3DocumentId?: string | null;
  acknowledgeMissingSigners: boolean;
}

export interface DsmAffectedAddendums {
  addendums: DsmAddendumSummary[];
  canVoid: boolean;
  blockReason: string | null;
}

export interface DsmSigningSession {
  envelopeId: string;
  signingUrl: string;
}

export const DSM_BLOCKED_REASON_LABELS: Record<string, string> = {
  [DsmBlockedAddendumReason.AlreadyInRequest]: 'Already in Envelope',
  [DsmBlockedAddendumReason.TypeExcluded]: 'Not included in APS',
  [DsmBlockedAddendumReason.NotEligible]: 'Not eligible for sign-off'
};

export const DSM_ENVELOPE_STATUS_LABELS: Record<string, string> = {
  [DsmEnvelopeStatus.Pending]: 'Pending',
  [DsmEnvelopeStatus.Sent]: 'Sent',
  [DsmEnvelopeStatus.Delivered]: 'Delivered',
  [DsmEnvelopeStatus.Complete]: 'Signed',
  [DsmEnvelopeStatus.Voided]: 'Voided',
  [DsmEnvelopeStatus.Declined]: 'Declined',
  [DsmEnvelopeStatus.Expired]: 'Expired'
};

export const DSM_ENVELOPE_STATUS_SEVERITIES: Record<string, DsmSignOffTagSeverity> = {
  [DsmEnvelopeStatus.Pending]: 'secondary',
  [DsmEnvelopeStatus.Sent]: 'info',
  [DsmEnvelopeStatus.Delivered]: 'info',
  [DsmEnvelopeStatus.Complete]: 'success',
  [DsmEnvelopeStatus.Voided]: 'danger',
  [DsmEnvelopeStatus.Declined]: 'danger',
  [DsmEnvelopeStatus.Expired]: 'danger'
};
